import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaUser, FaMapMarkerAlt, FaPhone, FaEnvelope, FaCalendarAlt, FaImage, FaFileAlt, FaShoppingCart } from 'react-icons/fa';
import { useTitle } from '../../../shared/context/TitleContext';
import { fetchPharmacyRequestDetails, clearRequestDetails } from '../../../redux/Slices/AdminPharmacyRequestsSlice';

const AdminPharmacyRequestDetails = () => {
  const { setTitle } = useTitle();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { requestId } = useParams();

  const { requestDetails, detailsLoading, detailsError } = useSelector(state => state.adminPharmacyRequests);

  useEffect(() => {
    setTitle('تفاصيل طلب الصيدلية');
    dispatch(clearRequestDetails());
    if (requestId) {
      dispatch(fetchPharmacyRequestDetails(requestId));
    }
  }, [requestId]);

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { text: 'في الانتظار', class: 'bg-warning' },
      'doctor_responded': { text: 'رد الصيدلية', class: 'bg-info' },
      'completed': { text: 'مكتمل', class: 'bg-success' },
      'cancelled': { text: 'ملغي', class: 'bg-danger' }
    };
    
    const statusInfo = statusMap[status] || { text: 'غير محدد', class: 'bg-secondary' };
    
    return (
      <span className={`badge ${statusInfo.class} text-white fs-6`}>
        {statusInfo.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'غير محدد';
    return new Date(dateString).toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    if (!price || price === 0) return 'غير محدد';
    return `${price} ج.م`;
  };

  if (detailsLoading) {
    return (
      <div className="container-fluid">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">جاري التحميل...</span>
          </div>
        </div>
      </div>
    );
  }

  if (detailsError) {
    return (
      <div className="container-fluid">
        <div className="alert alert-danger" role="alert">
          {detailsError}
        </div>
      </div>
    );
  }

  if (!requestDetails) {
    return (
      <div className="container-fluid">
        <div className="alert alert-warning" role="alert">
          لم يتم العثور على تفاصيل الطلب
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn btn-outline-secondary"
              onClick={() => navigate('/admin-pharmacy-requests')}
            >
              <FaArrowLeft className="mx-2" />
              العودة للقائمة
            </motion.button>
            <h4 className="mb-0">تفاصيل طلب الصيدلية #{requestDetails.id}</h4>
          </div>

          <div className="row">
            {/* Request Info */}
            <div className="col-md-8">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">معلومات الطلب</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">العميل:</label>
                        <div className="d-flex align-items-center">
                          <FaUser className="mx-2 text-primary" />
                          <span>{requestDetails.client || 'غير محدد'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">الحالة:</label>
                        <div>{getStatusBadge(requestDetails.status)}</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">تاريخ الطلب:</label>
                        <div className="d-flex align-items-center">
                          <FaCalendarAlt className="mx-2 text-primary" />
                          <span>{formatDate(requestDetails.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">المجموع الكلي:</label>
                        <div className="d-flex align-items-center">
                          <FaShoppingCart className="mx-2 text-primary" />
                          <span className="fw-bold text-success">{formatPrice(requestDetails.grand_total)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="mb-3">
                        <label className="form-label fw-bold">الملاحظة:</label>
                        <div className="d-flex align-items-center">
                          <FaFileAlt className="mx-2 text-primary" />
                          <span>{requestDetails.note || 'لا توجد ملاحظات'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Branch Info */}
              <div className="card mt-4">
                <div className="card-header">
                  <h5 className="mb-0">معلومات الصيدلية</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">اسم الصيدلية:</label>
                        <div className="fw-medium">{requestDetails.branch?.market_name || 'غير محدد'}</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">الهاتف:</label>
                        <div className="d-flex align-items-center">
                          <FaPhone className="mx-2 text-primary" />
                          <span>{requestDetails.branch?.phone || 'غير محدد'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">البريد الإلكتروني:</label>
                        <div className="d-flex align-items-center">
                          <FaEnvelope className="mx-2 text-primary" />
                          <span>{requestDetails.branch?.email || 'غير محدد'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="mb-3">
                        <label className="form-label fw-bold">العنوان:</label>
                        <div className="d-flex align-items-center">
                          <FaMapMarkerAlt className="mx-2 text-primary" />
                          <span>{requestDetails.branch?.address || 'غير محدد'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Items */}
              {requestDetails.items && requestDetails.items.length > 0 && (
                <div className="card mt-4">
                  <div className="card-header">
                    <h5 className="mb-0">عناصر الطلب</h5>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>اسم المنتج</th>
                            <th>الكمية</th>
                            <th>السعر</th>
                            <th>المجموع</th>
                            <th>ملاحظات</th>
                          </tr>
                        </thead>
                        <tbody>
                          {requestDetails.items.map((item, index) => (
                            <tr key={item.id}>
                              <td>{index + 1}</td>
                              <td>{item.product_name || 'غير محدد'}</td>
                              <td>{item.quantity || 'غير محدد'}</td>
                              <td>{formatPrice(item.price)}</td>
                              <td>{formatPrice(item.line_total)}</td>
                              <td>{item.meta?.note || 'لا توجد ملاحظات'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Images */}
            <div className="col-md-4">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">
                    <FaImage className="mx-2" />
                    الصور ({requestDetails.images?.length || 0})
                  </h5>
                </div>
                <div className="card-body">
                  {requestDetails.images && requestDetails.images.length > 0 ? (
                    <div className="row g-2">
                      {requestDetails.images.map((image, index) => (
                        <div key={index} className="col-6">
                          <img
                            src={image}
                            alt={`صورة ${index + 1}`}
                            className="img-fluid rounded"
                            style={{ height: '120px', objectFit: 'cover', width: '100%' }}
                            onClick={() => window.open(image, '_blank')}                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-muted py-4">
                      <FaImage className="fs-1 mb-3" />
                      <p>لا توجد صور</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPharmacyRequestDetails;
